<template>
  <ModalTopRight>
    <div ref="modalUserSettings" class="user-settings-modal">
      <div
        class="user-information-box"
        :class="{ 'user-information-box-no-logo': !realmLogo.length }"
      >
        <ContainerLogo v-if="realmLogo.length" class="logo-position" :logo-src="realmLogo" />
        <div class="user-information">
          <div class="user-heading">{{ t('general__text__user') }}</div>
          <div class="user-name">{{ nameOfCurrentUser ? nameOfCurrentUser : '' }}</div>
          <div class="user-name">{{ emailOfCurrentUser ? emailOfCurrentUser : '' }}</div>
        </div>
      </div>
      <hr class="divider" />
      <p class="header">{{ t('general__text__language-settings') }}</p>
      <DropdownStandard
        class="dropdown"
        :title="languageOptionsTitle"
        :options="languageOptions"
        @select="handleLanguageSelection"
      />
      <div class="user-links-box">
        <div class="user-links">
          <ButtonText
            :buttonText="t('general__text__support')"
            :iconLeft="iconMail"
            size="small"
            @onClicked="handleSupport"
          />
        </div>
        <div class="user-links">
          <ButtonText
            :buttonText="t('general__text__logout')"
            :iconLeft="iconLogOut"
            size="small"
            @onClicked="signOut"
          />
        </div>
      </div>
    </div>
  </ModalTopRight>
</template>

<script lang="ts" setup>
import ModalTopRight from '@/design-system/templates/ModalTopRight.vue'
import DropdownStandard from '@/design-system/molecules/DropdownStandard.vue'
import ContainerLogo from '@/design-system/atoms/ContainerLogo.vue'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import i18n from '@/i18n'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { postUserLanguage } from '@/services/realms.service'
import { clearLocalStorage } from '@/utils/localstorage'
import { getRealmId } from '@/utils/realmId'
import iconMail from '@/design-system/assets/icons/Icon-Mail.svg'
import iconLogOut from '@/design-system/assets/icons/Icon-Log-Out.svg'

const store = useStore()
const { t } = useI18n()

interface EMITS {
  (e: 'closeMenu')
}
const emits = defineEmits<EMITS>()

const languageOptions = ref<any>([
  {
    disabled: false,
    id: 'sv',
    name: t('general__language__swedish'),
    selected: false,
    value: 1
  },
  {
    disabled: false,
    id: 'en',
    name: t('general__language__english'),
    selected: false,
    value: 2
  }
])

const i18nLanguage = computed(() => i18n.global.locale.value)
const initialLoad = ref<boolean>(true)
const emailOfCurrentUser = computed<string>(() => store.state.oidcStore?.user?.preferred_username)
// sub (subject) equals userId
const idOfCurrentUser = computed<string>(() => store.state.oidcStore?.user?.sub)
const languageOptionsTitle = computed(() => {
  const currentLanguage = languageOptions.value.find((language) => language.selected)
  return currentLanguage?.name ? currentLanguage.name : ''
})

const modalUserSettings = ref<HTMLElement | null>(null)
const nameOfCurrentUser = computed<string>(() => store.state.oidcStore?.user?.name)
const realmId = computed(() => getRealmId())
const realmLogo = computed<string>(() => store.getters.realmLogo)

function closeMenu(event: MouseEvent | TouchEvent) {
  if (initialLoad.value) {
    initialLoad.value = false
    return
  }
  const target = event.target as HTMLElement
  if (!modalUserSettings.value?.contains(target)) {
    emits('closeMenu')
  }
}
function handleLanguageSelection(selectedValue) {
  if (!realmId.value) return
  if (selectedValue !== i18nLanguage.value) {
    postUserLanguage(realmId.value, idOfCurrentUser.value, selectedValue)
    i18n.global.locale.value = selectedValue
    store.commit('setInfoMessage', {
      type: 'success',
      message: t('message__language-changed')
    })
  }
}
function handleSupport() {
  window.open('mailto: support@digitalbuildings.ai?subject=ZDB support')
}
function signOut() {
  const id_token = store.state.oidcStore.id_token
  clearLocalStorage()
  store.dispatch('signOutOidc', { id_token_hint: id_token })
}
onMounted(() => {
  document.addEventListener('click', closeMenu)
  languageOptions.value.forEach((language) => {
    if (language.id === i18nLanguage.value) language.selected = true
  })
})
onUnmounted(() => document.removeEventListener('click', closeMenu))
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
.divider {
  border: variables.$border-width-xsmall solid variables.$color-greyscale-line-grey;
  margin-left: calc(#{variables.$margin-xlarge} * -1);
  margin-right: calc(#{variables.$margin-xlarge} * -1);
  width: calc(#{100%} + #{variables.$margin-xlarge} + #{variables.$margin-xlarge});
}
.dropdown {
  margin-bottom: variables.$margin-medium;
  width: 100%;
}
.header {
  @include font-mixins.desktop-bold-paragraph;
  color: variables.$color-greyscale-content;
}
.user-settings-modal {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: variables.$padding-xlarge;
}
.user-heading {
  @include font-mixins.desktop-bold-label;
  color: variables.$color-text-secondary;
}
.user-information {
  display: flex;
  flex-direction: column;
  gap: variables.$gap-large;
  padding: variables.$padding-medium;
}
.user-information-box {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
}
.user-links-box {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: variables.$padding-medium 0;
  gap: variables.$gap-xlarge;
}
.logo-position {
  margin: auto variables.$margin-medium;
}
.user-links {
  color: variables.$color-primary;
  cursor: pointer;
  display: flex;
  gap: variables.$gap-medium;
}
.user-name {
  @include font-mixins.desktop-bold-label;
  color: variables.$color-text-primary;
}
.user-information-box-no-logo {
  padding-left: variables.$padding-large;
}
</style>
